/**
 * The **asset card** — frame 08 of the Equipment page design, *One asset, every field*
 * (`equipment-page-asset-card`, R4–R17). A butter `DockedCard` beside the Rows view, opened by a row
 * click, holding every field of one register row:
 *
 * - **Name** and **Asset Identifier**, with the table cell's own answers (`nameAnswer`,
 *   `identifierAnswer`) and writes (`useEquipmentRowWrites`);
 * - **Product**, through the identify card's picker (`IdentifyProductField`) and the machine card's
 *   change (`useRowProductChange`), with *Not tracked* last;
 * - **From the product** — capacity and the 3D model, each the product's unless overridden
 *   (`OverrideRows`);
 * - **Plan** — where it is placed, and its BID; a row kept off the plan shows that as an override
 *   row with *Put Back On Plan* (`equipment-page-keep-off-plan-undo`, R8);
 * - **Costs and Dates** and **Notes**, folded.
 *
 * **Every field saves when it is left** (blur, or Enter outside Notes), one `PATCH` carrying that field
 * alone; an unchanged value sends nothing. A refusal stays under its field, with what was typed, and
 * is said in the card's alert region. *Done* saves the field being edited and closes the card.
 *
 * It writes nothing itself: the host hands it the writes and the identify binding, as the identify
 * card's host does. The host keeps it mounted while the open row moves, so the folds stay as they
 * were; a new row brings new drafts.
 */
import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';
import {
  Button,
  Combobox,
  DateField,
  DockedCard,
  Field,
  Icon,
  Input,
  Note,
  OverrideRows,
  Textarea,
  useToast,
} from '@repo/design-system';
import { fontMono, ink, semantic, space, typeScale, weight } from '@repo/design-tokens';
import { useEquipmentCatalogStore } from '@repo/front-end-state';
import {
  Facts,
  IdentifyProductField,
  NOT_TRACKED_VALUE,
  useRowProductChange,
  type IdentifyPickerFetchers,
  type IdentifyPickerOption,
  MODEL_PICKER_EXACT_MATCH,
  MODEL_PICKER_FILTER,
  modelPickerEmptyText,
  useModelPickerOptions,
} from '@repo/ui';
import {
  isAlreadyIdentifiedError,
  isProductChangedError,
  type IdentifyProduct,
  type TwinAssetReferenceSourceTag,
  type TwinAssetResponse,
} from '@repo/front-end-services';
import {
  assetPlanStateFor,
  emptyPlacementIndex,
  placementIndexFor,
  placementWhereFor,
  registerRowName,
  type AssetIdentityState,
  type AssetPlanState,
} from '@repo/front-end-utils';
import { modelFileOptions, type FloorPlannerLayout } from '@repo/frontend-types';

import {
  identifierAnswer,
  identifierAnswerCommits,
  nameAnswer,
  nameAnswerCommits,
  nameIndexWithout,
  type NameAnswer,
} from './equipmentRowEdit';
import {
  COST_AND_DATE_FIELDS,
  OVERRIDE_LABELS,
  filledCount,
  overrideAside,
  overrideRowsFor,
  productLabel,
  youWrote,
  type CostAndDateField,
  type OverrideKind,
} from './equipmentAssetCardRows';
import {
  NOT_SURE,
  type CellWriteOutcome,
  type FieldWriteOutcome,
  type RowWriteFields,
} from './useEquipmentRowWrites';
import { showsDoesntFit } from './equipmentRegisterColumns';
import { KEEP_OFF_UNIDENTIFIED } from './useEquipmentBulkWrites';

/** The Product field's source line (`equipment-page-adoption`, R10), beside what the customer wrote. */
const SOURCE_LINES: Record<TwinAssetReferenceSourceTag, string> = {
  bi: 'From BI',
  file: 'From the adoption file',
  lookup: 'Found by lookup',
};

/** The Keep note's refusals (`equipment-page-adoption`, the Failure States table). */
export const KEEP_CHANGED = 'This asset’s product changed. Have another look.';
export const KEEP_REFUSED = 'Couldn’t keep this product.';
export const KEEP_UNSAVED = 'Couldn’t save. Try again.';
/**
 * The row's placement index for the plan state: a known plan with no layout is a twin with no
 * floor plan, where nothing is placed — as the bar reads it (`buildIdentifyModel`'s `planAbsent`).
 */
const planIndexFor = (layout: FloorPlannerLayout | null, planKnown: boolean) =>
  planKnown ? (placementIndexFor(layout) ?? emptyPlacementIndex()) : null;

/** *Put Back On Plan*'s refusal (`equipment-page-keep-off-plan-undo`, R9). */
export const PUT_BACK_UNSAVED = 'Couldn’t put this back on the plan. Try again.';

const keepRefusal = (error: unknown): string => {
  if (isProductChangedError(error)) return KEEP_CHANGED;
  const status = (error as { status?: unknown } | null)?.status;
  return status === 400 ? KEEP_REFUSED : KEEP_UNSAVED;
};

/** The writes the card makes — `useEquipmentRowWrites`'s, bound by the host. */
export interface AssetCardWrites {
  rename: (rowId: number, answer: NameAnswer) => Promise<CellWriteOutcome>;
  setIdentifier: (rowId: number, value: string | null) => Promise<CellWriteOutcome>;
  setFields: (
    rowId: number,
    fields: RowWriteFields,
    options?: { precondition?: () => boolean; carried?: () => Promise<void> },
  ) => Promise<FieldWriteOutcome>;
}

/** The identify binding the card's Product field and its first identification use. */
export interface AssetCardIdentify {
  fetchers: IdentifyPickerFetchers;
  identifyRow: (
    rowId: number,
    referenceId: string,
    label: string,
    product: IdentifyProduct | undefined,
  ) => Promise<'moved' | 'stayed'>;
  busy: boolean;
  organisationName?: string | null;
  logContext?: Readonly<Record<string, string | number>>;
}

export interface AssetCardFolds {
  costs: boolean;
  notes: boolean;
}

/** What the host calls before the open row moves (R3), and *Done*. */
export interface EquipmentAssetCardHandle {
  /** Saves every field holding an unsaved edit. Resolves false when any was refused. */
  commitPending: () => Promise<boolean>;
}

export interface EquipmentAssetCardProps {
  row: TwinAssetResponse;
  /** The register as last read: the name index, the identifier rule and the Undo guard read it. */
  rows: readonly TwinAssetResponse[];
  layout: FloorPlannerLayout | null;
  /** The row's identity state, from the step's one identify model. Null where it could not count. */
  state: AssetIdentityState | null;
  /** Whether the plan's placements are known (the coverage index's `planKnown`). */
  planKnown: boolean;
  /**
   * The plan state as it is NOW, read by a put back's Undo: the toast outlives this component (the
   * step can change and a row be placed meanwhile), so the page's getter, never this render's.
   */
  livePlanStateOf?: (id: number) => AssetPlanState | undefined;
  writes: AssetCardWrites;
  identify: AssetCardIdentify;
  /** The twin's 3D model files (`getModelFiles`), loaded on the first *Change* of the 3D model. */
  loadModelFiles: (options?: { refresh?: boolean }) => Promise<string[]>;
  /**
   * A row with NO product had its 3D model cleared (ruled in the build, 2026-10-20): the host clears
   * the plan's copy too, as the edit dialog's save did (Rob, 2026-10-06).
   */
  onModelCleared?: (rowId: number) => void;
  /** The folds' open state — the host's, so it survives the card moving between rows (R16). */
  folds: AssetCardFolds;
  onFoldsChange: (folds: AssetCardFolds) => void;
  onClose: () => void;
  /** Where focus goes when the card closes: the open row. */
  returnFocusTo?: { readonly current: HTMLElement | null };
  /**
   * *Remove From Register*, the host's (`EquipmentAssetRemove`), on the footer's left. Handed the
   * card's alert, for a refusal said before any modal opens.
   */
  removeControl?: (refuse: (message: string) => void) => React.ReactNode;
  /** The step's toast region for *Product changed*, or the viewport stack. */
  toastRegion?: string;
}

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${space[4]};
`;

/** The Keep note's sentence, with its button after it (wraps on a narrow card). */
const KeepLine = styled.span`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: ${space[2]};
  width: 100%;
`;

const Foot = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: ${space[3]};
`;

const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${space[2]};
`;

const SectionTitle = styled.h4`
  margin: 0;
  color: ${ink[2]};
  font-size: ${typeScale.caption1.fontSize};
  line-height: ${typeScale.caption1.lineHeight};
  font-weight: ${weight.semibold};
`;

/** Frame 08 draws the two fold toggles on one line; an open fold's body runs full width below. */
const Folds = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${space[2]};
`;

const FoldToggles = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${space[3]};
`;

const FoldBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${space[3]};
  padding-left: ${space[4]};

  &[hidden] {
    display: none;
  }
`;

const Mono = styled.span`
  font-family: ${fontMono};
`;

/** Announcements the card makes — one polite region, one assertive (Block A). */
const Live = styled.div`
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
`;

const COST_LABELS: Record<CostAndDateField, string> = {
  unit_cost: 'Unit cost',
  residual_value: 'Residual value',
  currency: 'Currency',
  install_date: 'Installed',
  manufacture_date: 'Manufactured',
  warranty_end_date: 'Warranty ends',
  contract_end_date: 'Contract ends',
  condition: 'Condition',
};

const DATE_FIELDS: ReadonlySet<CostAndDateField> = new Set<CostAndDateField>([
  'install_date',
  'manufacture_date',
  'warranty_end_date',
  'contract_end_date',
]);

/** The server's cap on `customer_notes`. */
export const NOTES_MAX = 4000;

export const NAME_CANT_BE_EMPTY = 'Name can’t be empty';
export const CAPACITY_RULE = 'A whole number, 1 or more';

const asText = (value: unknown): string =>
  value === null || value === undefined ? '' : String(value);

/** How long a product change waits for the register re-read to carry it before raising its toast. */
const REREAD_WAIT_MS = 5000;

/**
 * Resolves once the register store shows `rowId` carrying `referenceId` — or after
 * `REREAD_WAIT_MS`, when the toast goes up regardless and its Undo checks the register as it is.
 */
const registerCarries = (rowId: number, referenceId: string | undefined): Promise<void> =>
  new Promise(resolve => {
    const carries = () =>
      useEquipmentCatalogStore.getState().registerRows.find(r => r.id === rowId)?.reference_id ===
      referenceId;
    if (referenceId === undefined || carries()) {
      resolve();
      return;
    }
    let unsubscribe = () => undefined as void;
    const timer = window.setTimeout(() => {
      unsubscribe();
      resolve();
    }, REREAD_WAIT_MS);
    unsubscribe = useEquipmentCatalogStore.subscribe(() => {
      if (!carries()) return;
      window.clearTimeout(timer);
      unsubscribe();
      resolve();
    });
  });

/**
 * The picked option as the toast's product: its label is the product and its meta leads with the
 * manufacturer (*Life Fitness · Signature*), which is what *Product changed*'s second line names.
 */
const productOf = (
  value: string,
  option: IdentifyPickerOption | null,
): IdentifyProduct | undefined =>
  option
    ? {
        referenceId: value,
        product: option.label,
        productLine: null,
        manufacturer: option.meta?.split(' · ')[0] ?? null,
        capacity: null,
        hasModel: false,
      }
    : undefined;

/**
 * A money value as sent: a number where it parses, else the text, so the server's sentence answers —
 * the edit dialog's rule. Nothing is stripped: *12,5* (a decimal comma) is sent as text and refused,
 * never read as 125.
 */
export const moneyValue = (draft: string): number | string | null => {
  const trimmed = draft.trim();
  if (trimmed === '') return null;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : trimmed;
};

/** A field's draft, its saving and its refusal; `commit` saves it, or resolves true with nothing to do. */
interface CardField {
  value: string;
  error: string | null;
  saving: boolean;
  onChange: (next: string) => void;
  commit: () => Promise<boolean>;
}

/**
 * One editable field (R6). `save` resolves null when it landed, or the sentence it was refused with.
 * An unchanged value (after trimming) sends nothing. A new row brings a new draft.
 */
const useCardField = (
  rowId: number,
  saved: string,
  save: (draft: string) => Promise<string | null>,
  register: (commit: () => Promise<boolean>) => () => void,
): CardField => {
  const [draft, setDraft] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    setDraft(null);
    setError(null);
  }, [rowId]);

  /*
   * The value a save was accepted with, until the register's re-read carries it: compared against,
   * and shown, in place of the stale prop. Otherwise putting the old value back while the re-read is
   * on its way compares equal to the old prop and sends nothing, and the field flickers back to it
   * (Copilot, PR #2177).
   */
  const accepted = useRef<{ rowId: number; value: string; over: string } | null>(null);
  const held = accepted.current;
  const baseline = held && held.rowId === rowId && held.over === saved ? held.value : saved;
  if (held && baseline === saved) accepted.current = null;

  const live = useRef({ draft, saved: baseline, prop: saved, save, rowId });
  live.current = { draft, saved: baseline, prop: saved, save, rowId };
  /** The save in flight: which row and draft it is for, and its outcome. */
  const inFlight = useRef<{ forRow: number; draft: string; outcome: Promise<boolean> } | null>(
    null,
  );

  /*
   * A commit started on one row can answer after the card has moved to the next (R3: the arrows
   * commit the draft, then move). Its answer is then the old row's: nothing is drawn on this row's
   * field — the card's alert names the row it was for.
   */
  /*
   * A commit made while this field's save is in flight WAITS for it (pressing Done blurs the field,
   * which starts the save, then clicks): the same edit answers with that save's outcome, so a
   * refusal keeps the card open; a newer edit, or the next row's, is sent once it has answered.
   */
  const commit = useCallback(async (): Promise<boolean> => {
    // The field as it stood when the commit was asked for. Moving to another row while an earlier
    // save is answered resets the draft; this commit still owes the departing row its newer edit
    // (Copilot, PR #2177).
    const asked = live.current;
    const latest = () => (live.current.rowId === asked.rowId ? live.current : asked);
    for (;;) {
      const running = inFlight.current;
      if (!running) break;
      const outcome = await running.outcome;
      const { draft: after, rowId } = latest();
      if (after === null || (after === running.draft && rowId === running.forRow)) return outcome;
    }
    const { draft: now, saved: was, prop: over, save: send, rowId: forRow } = latest();
    if (now === null) return true;
    if (now.trim() === was.trim()) {
      setDraft(null);
      setError(null);
      return true;
    }
    const run = (async () => {
      setSaving(true);
      try {
        const refusal = await send(now);
        const here = live.current.rowId === forRow;
        if (refusal === null) {
          if (here) {
            accepted.current = { rowId: forRow, value: now, over };
            setDraft(current => (current === now ? null : current));
            setError(null);
          }
          return true;
        }
        if (here) setError(refusal);
        return false;
      } finally {
        setSaving(false);
      }
    })();
    const entry = { forRow, draft: now, outcome: run };
    inFlight.current = entry;
    try {
      return await run;
    } finally {
      if (inFlight.current === entry) inFlight.current = null;
    }
  }, []);

  useEffect(() => register(commit), [register, commit]);

  return {
    value: draft ?? baseline,
    error,
    saving,
    onChange: next => {
      setDraft(next);
      setError(null);
    },
    commit,
  };
};

export const EquipmentAssetCard = React.forwardRef<
  EquipmentAssetCardHandle,
  EquipmentAssetCardProps
>(function EquipmentAssetCard(
  {
    row,
    rows,
    layout,
    state,
    planKnown,
    livePlanStateOf,
    writes,
    identify,
    loadModelFiles,
    onModelCleared,
    folds,
    onFoldsChange,
    onClose,
    returnFocusTo,
    removeControl,
    toastRegion,
  },
  ref,
) {
  const name = registerRowName(row);
  const [status, setStatus] = useState('');
  const [alert, setAlert] = useState('');
  const announce = useCallback((message: string) => {
    // Cleared first, so the same sentence twice is announced twice.
    setStatus('');
    window.setTimeout(() => setStatus(message), 0);
  }, []);
  const refuse = useCallback((message: string) => {
    setAlert('');
    window.setTimeout(() => setAlert(message), 0);
  }, []);
  useEffect(() => {
    setAlert('');
    setStatus('');
  }, [row.id]);

  // The latest register, for the Undo guard and the identifier rule, read when they run.
  const rowsRef = useRef(rows);
  rowsRef.current = rows;
  // The row on show NOW: a product Undo answering after the card moved says nothing on this row.
  const openRowId = useRef(row.id);
  openRowId.current = row.id;

  /* ---- the registry of fields with an unsaved edit (R3, Done) ---- */
  const commits = useRef(new Set<() => Promise<boolean>>());
  const register = useCallback((commit: () => Promise<boolean>) => {
    commits.current.add(commit);
    return () => {
      commits.current.delete(commit);
    };
  }, []);
  /*
   * Writes already sent — a date, a product, an override — that have not answered. Done waits for
   * them too, and stays open on a refusal, so the field's error is never drawn on a closed card
   * (Copilot, PR #2177).
   */
  const sending = useRef(new Set<Promise<boolean>>());
  const track = useCallback(<T,>(write: Promise<T>, landed: (result: T) => boolean): Promise<T> => {
    const outcome = write.then(landed, () => false);
    sending.current.add(outcome);
    void outcome.then(() => sending.current.delete(outcome));
    return write;
  }, []);
  const commitPending = useCallback(async (): Promise<boolean> => {
    // forEach, never a spread or for…of: this app compiles to ES5 without downlevelIteration, where
    // iterating a Set that way yields nothing.
    const pending: Array<Promise<boolean>> = [];
    sending.current.forEach(outcome => pending.push(outcome));
    commits.current.forEach(commit => pending.push(commit()));
    const results = await Promise.all(pending);
    return results.every(Boolean);
  }, []);
  useImperativeHandle(ref, () => ({ commitPending }), [commitPending]);

  /** A write's outcome, said and returned as the field's refusal: null when it landed (R6). */
  const settle = useCallback(
    async (
      label: string,
      run: () => Promise<{ ok: true } | { ok: false; skipped?: boolean; line?: string }>,
    ): Promise<string | null> => {
      announce('Saving…');
      const outcome = await track(
        run(),
        result => result.ok || ('skipped' in result && !!result.skipped),
      );
      if (outcome.ok) {
        announce('Saved');
        return null;
      }
      if (outcome.skipped) return null;
      const line = outcome.line ?? 'The change was not saved.';
      refuse(`${label} of ${name}: ${line}`);
      return line;
    },
    [name, announce, refuse, track],
  );
  /** A one-field write (R6). */
  const fieldWrite = useCallback(
    (fields: RowWriteFields, label: string): Promise<string | null> =>
      settle(label, () => writes.setFields(row.id, fields)),
    [settle, writes, row.id],
  );

  /* ---- Name (R7) ---- */
  const nameIndex = useMemo(() => nameIndexWithout(layout, rows, row.id), [layout, rows, row.id]);
  const nameField = useCardField(
    row.id,
    name,
    async draft => {
      const answer = nameAnswer(draft, row, nameIndex);
      if (answer.kind === 'empty') {
        refuse(`Name of ${name}: ${NAME_CANT_BE_EMPTY}`);
        return NAME_CANT_BE_EMPTY;
      }
      if (!nameAnswerCommits(answer)) return null;
      return settle('Name', () => writes.rename(row.id, answer));
    },
    register,
  );
  const typedName = nameField.value;
  const nameLine = useMemo(() => {
    if (typedName.trim() === name.trim()) return null;
    const answer = nameAnswer(typedName, row, nameIndex);
    return answer.kind === 'empty' ? null : answer.line;
  }, [typedName, name, row, nameIndex]);

  /* ---- Asset Identifier (R8) ---- */
  const savedIdentifier = asText(row.customer_asset_identifier);
  const identifierField = useCardField(
    row.id,
    savedIdentifier,
    async draft => {
      const answer = identifierAnswer(draft, row, rowsRef.current);
      if (answer.kind === 'taken') {
        refuse(`Asset Identifier of ${name}: ${answer.line}`);
        return answer.line;
      }
      if (!identifierAnswerCommits(answer)) return null;
      // Empty sends null, never '': the twin's partial unique index would treat '' as a value.
      return settle('Asset Identifier', () => writes.setIdentifier(row.id, answer.value));
    },
    register,
  );
  const identifierLine = useMemo(() => {
    if (identifierField.value.trim() === savedIdentifier.trim()) return null;
    const answer = identifierAnswer(identifierField.value, row, rows);
    return answer.kind === 'taken' ? null : answer.line;
  }, [identifierField.value, savedIdentifier, row, rows]);

  /* ---- Product (R9–R11) ---- */
  const [chosenProduct, setChosenProduct] = useState<string | null>(null);
  const [productError, setProductError] = useState<string | null>(null);
  const [productBusy, setProductBusy] = useState(false);
  // Declared here: the product field reads it (the two never write at once).
  const [overrideBusy, setOverrideBusy] = useState(false);
  useEffect(() => {
    setChosenProduct(null);
    setProductError(null);
  }, [row.id, row.reference_id, row.match_status]);
  const toast = useToast();
  const { change } = useRowProductChange({
    row,
    identifyRow: identify.identifyRow,
    write: async (fields, options) => {
      const forRow = row.id;
      /*
       * The toast's Undo checks the register: the row's queue waits for the re-read this write
       * started to carry the change before the next write takes its turn. An Undo pressed at once
       * is then not told the row "has changed since", and an Undo queued behind a newer change
       * reads that change, never the one before it (Copilot, PR #2177).
       */
      const outcome = await writes.setFields(forRow, fields, {
        ...options,
        carried: () => registerCarries(forRow, fields.reference_id),
      });
      if (outcome.ok) return true;
      if (!outcome.skipped && openRowId.current === forRow) {
        setProductError(outcome.line);
        refuse(`Product of ${name}: ${outcome.line}`);
      }
      return false;
    },
    // LIVE, from the register store, never this card's props: the toast's Undo outlives the card.
    currentRow: rowId =>
      useEquipmentCatalogStore.getState().registerRows.find(candidate => candidate.id === rowId),
    toastRegion,
  });
  const notTracked = state === 'not_tracked';
  /** Resolves true when the mark landed. No Undo (ruled 2026-10-15); its toast says it landed. */
  const markNotTracked = useCallback(async (): Promise<boolean> => {
    const forRow = row.id;
    // Only a row with no product is guarded: on an identified row the mark replaces its product.
    const fields: RowWriteFields = row.reference_id
      ? { noCatalogueProduct: true }
      : { noCatalogueProduct: true, ifUnidentified: true };
    const outcome = await writes.setFields(forRow, fields);
    if (outcome.ok) {
      toast.show({
        tone: 'success',
        icon: <Icon name="ban" />,
        title: `${name} is Not tracked`,
        ...(toastRegion ? { region: toastRegion } : {}),
      });
      return true;
    }
    if (outcome.skipped) return false;
    const line = isAlreadyIdentifiedError(outcome.error)
      ? `${name} was identified meanwhile — nothing changed`
      : outcome.line;
    refuse(`Product of ${name}: ${line}`);
    // An answer for a row the card has left is said in the alert, which names it, and drawn nowhere.
    if (openRowId.current === forRow) setProductError(line);
    return false;
  }, [row.reference_id, row.id, writes, name, refuse, toast, toastRegion]);
  const chooseProduct = useCallback(
    async (value: string | null, option: IdentifyPickerOption | null) => {
      if (!value || productBusy || identify.busy || overrideBusy) return;
      if (value === row.reference_id) return;
      setChosenProduct(value);
      setProductError(null);
      setProductBusy(true);
      const run = async (): Promise<boolean> => {
        if (value === NOT_TRACKED_VALUE) {
          const landed = await markNotTracked();
          if (!landed) setChosenProduct(null);
          return landed;
        }
        const outcome = await change(value, productOf(value, option), `“${name}”`);
        if (outcome === 'failed' || outcome === 'stayed') setChosenProduct(null);
        // `stayed`: the identify route answered without writing — a refusal, for Done as for the field.
        return outcome !== 'failed' && outcome !== 'stayed';
      };
      try {
        await track(run(), landed => landed);
      } finally {
        setProductBusy(false);
      }
    },
    [
      productBusy,
      identify.busy,
      overrideBusy,
      row.reference_id,
      markNotTracked,
      change,
      name,
      track,
    ],
  );
  const current = useMemo(() => {
    const label = productLabel(row);
    return row.reference_id && label ? { referenceId: row.reference_id, label } : null;
  }, [row]);
  const wrote = youWrote(row);
  const sourceTag = row.reference_source_tag;
  const sourceLine = sourceTag ? (SOURCE_LINES[sourceTag] ?? null) : null;
  const productAside = [sourceLine, wrote].filter(Boolean).join(' · ') || undefined;

  /* ---- Keep This Product (`equipment-page-adoption`, R9) ---- */
  // The row a Keep is in flight for: busy is per row, so the card moving on never shows it busy.
  const [keepBusyFor, setKeepBusyFor] = useState<number | null>(null);
  const keepBusy = keepBusyFor === row.id;
  // The rows a Keep is in flight for, read synchronously: a second press before the re-render
  // that shows `loading` sends nothing.
  const keepSending = useRef(new Set<number>());
  const [keepError, setKeepError] = useState<string | null>(null);
  /*
   * A Keep was pressed and its note may go: on success the re-read row is kept and the note
   * unmounts, on a 409 the row may no longer be flagged. Either way focus would fall to the body;
   * the effect below puts it on the card's next control instead (review, H1).
   */
  const keepFocusPending = useRef(false);
  useEffect(() => {
    setKeepError(null);
    keepFocusPending.current = false;
  }, [row.id]);
  const fitDetail = row.doesnt_fit_detail ?? null;
  const flagged = showsDoesntFit(row, state);
  const keptProduct = productLabel(row) ?? asText(row.product);
  const keepProduct = useCallback(async () => {
    const forRow = row.id;
    const expectedReferenceId = row.reference_id;
    // The re-entry guard: the button stays enabled while busy, so focus never leaves it (H1).
    if (keepSending.current.has(forRow) || !expectedReferenceId) return;
    keepSending.current.add(forRow);
    setKeepError(null);
    setKeepBusyFor(forRow);
    keepFocusPending.current = true;
    const outcome = await track(
      // The name the card showed, beside the product: a rename since then refuses with 409.
      writes.setFields(forRow, { keepFit: true, expectedReferenceId, expectedName: row.name }),
      result => result.ok || ('skipped' in result && !!result.skipped),
    ).finally(() => {
      keepSending.current.delete(forRow);
      setKeepBusyFor(current => (current === forRow ? null : current));
    });
    if (outcome.ok) {
      // The toast speaks in its own polite region; the card says nothing over it.
      toast.show({
        tone: 'neutral',
        icon: <Icon name="check" />,
        title: `Kept ${keptProduct} for ${name}.`,
        inlineAction: {
          label: 'Undo',
          onAction: () => {
            void writes.setFields(forRow, { keepFit: false }).then(undone => {
              if (undone.ok) announce('Undone.');
              else if (!undone.skipped) refuse(`Product of ${name}: ${KEEP_UNSAVED}`);
            });
          },
        },
        ...(toastRegion ? { region: toastRegion } : {}),
      });
      return;
    }
    if (outcome.skipped) return;
    // The write re-reads the register whatever it answers, so a 409 shows the product it changed to.
    const line = keepRefusal(outcome.error);
    refuse(`Product of ${name}: ${line}`);
    if (openRowId.current === forRow) setKeepError(line);
  }, [
    row.id,
    row.reference_id,
    row.name,
    track,
    writes,
    announce,
    toast,
    keptProduct,
    name,
    refuse,
    toastRegion,
  ]);
  /** A To confirm row's stored guess, on top of the picker as confirm's *Change* puts it (R10). */
  const proposal = useMemo(
    () =>
      state === 'to_confirm' && row.match_proposal
        ? {
            reference_id: row.match_proposal.reference_id,
            manufacturer: row.match_proposal.manufacturer,
            product_line: row.match_proposal.product_line,
            product: row.match_proposal.product,
            reason: row.match_proposal.reason,
          }
        : null,
    [state, row.match_proposal],
  );

  /* ---- From the product (R12–R14) ---- */
  const [editing, setEditing] = useState<OverrideKind | null>(null);
  const [capacityDraft, setCapacityDraft] = useState('');
  const [capacityError, setCapacityError] = useState<string | null>(null);
  const [modelFiles, setModelFiles] = useState<string[] | null>(null);
  const [modelsError, setModelsError] = useState<string | null>(null);
  const [modelsLoading, setModelsLoading] = useState(false);
  const overridesRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    setEditing(null);
    setCapacityError(null);
  }, [row.id]);

  const loadModels = useCallback(
    async (refresh = false) => {
      setModelsLoading(true);
      setModelsError(null);
      try {
        setModelFiles(await loadModelFiles(refresh ? { refresh: true } : undefined));
      } catch {
        setModelsError('Couldn’t load the models');
      } finally {
        setModelsLoading(false);
      }
    },
    [loadModelFiles],
  );
  /*
   * The model list is read when the card opens, not on the first *Change*: the rows show a model by
   * its readable name (R12), which needs the list. One listing, cached for 60 seconds by the page's
   * `loadModelFiles`; a failure leaves the file names showing and *Change* offers Retry.
   */
  useEffect(() => {
    void loadModels();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once per card, not per row
  }, []);
  const modelOptions = useModelPickerOptions(modelFiles ?? [], 'register');
  const modelName = useMemo(() => {
    const labels = new Map(
      modelFileOptions(modelFiles ?? []).map(option => [option.value, option.label]),
    );
    // A file the listing does not hold is named by the same rule on its own (the rule strips only
    // `.glb`, so any extension is read as one), and by its file name where the rule cannot read it.
    return (file: string) =>
      labels.get(file) ??
      modelFileOptions([file.replace(/\.[a-z0-9]+$/i, '.glb')])[0]?.label ??
      file;
  }, [modelFiles]);

  const costsToggleRef = useRef<HTMLButtonElement>(null);
  /*
   * Back to the row's action — or, where that action is not rendered, to the next action in the
   * card, so focus never falls out of it.
   */
  const focusOverrideAction = useCallback((kind: OverrideKind) => {
    window.setTimeout(() => {
      const scope = overridesRef.current;
      const own = scope?.querySelector<HTMLElement>(`[data-override-row="${kind}"]`);
      const next = own ?? scope?.querySelector<HTMLElement>('[data-override-row]');
      (next ?? costsToggleRef.current)?.focus();
    }, 0);
  }, []);
  /*
   * The Keep note went while focus was on it (or had already fallen to the body): the next control
   * in the card takes it, as `focusOverrideAction` does. Not the product picker above: its
   * combobox opens on focus, and an auto-highlighted guess would make the next Enter a product
   * write.
   */
  useEffect(() => {
    if (flagged || !keepFocusPending.current) return;
    keepFocusPending.current = false;
    const active = document.activeElement;
    if (active && active !== document.body) return;
    const next = overridesRef.current?.querySelector<HTMLElement>('[data-override-row]');
    (next ?? costsToggleRef.current)?.focus();
  }, [flagged]);
  // The 3D editor takes focus when it opens, as the capacity editor does (`autoFocus`).
  const modelEditorRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (editing !== 'model_3d') return;
    modelEditorRef.current?.querySelector<HTMLInputElement>('input[role="combobox"]')?.focus();
  }, [editing]);

  const overrideWrite = useCallback(
    async (kind: OverrideKind, value: number | string | null): Promise<boolean> => {
      setOverrideBusy(true);
      try {
        const fields = { [kind]: value } as RowWriteFields;
        const refusal = await fieldWrite(fields, OVERRIDE_LABELS[kind]);
        if (refusal === null && value === null) {
          announce(`${OVERRIDE_LABELS[kind]} uses the product’s again`);
        }
        return refusal === null;
      } finally {
        setOverrideBusy(false);
      }
    },
    [fieldWrite, announce],
  );

  const startChange = useCallback(
    (kind: OverrideKind) => {
      setEditing(kind);
      if (kind === 'capacity') {
        setCapacityDraft(asText(row.capacity));
        setCapacityError(null);
      } else if (modelFiles === null && !modelsLoading) {
        void loadModels();
      }
    },
    [row.capacity, modelFiles, modelsLoading, loadModels],
  );
  const cancelEdit = useCallback(() => {
    const kind = editing;
    setEditing(null);
    setCapacityError(null);
    if (kind) focusOverrideAction(kind);
  }, [editing, focusOverrideAction]);

  /** Resolves true when there was nothing to send or it landed. */
  const commitCapacity = useCallback(async (): Promise<boolean> => {
    const trimmed = capacityDraft.trim();
    if (trimmed === asText(row.capacity)) {
      cancelEdit();
      return true;
    }
    if (!/^\d+$/.test(trimmed) || Number(trimmed) < 1) {
      setCapacityError(CAPACITY_RULE);
      refuse(`Capacity of ${name}: ${CAPACITY_RULE}`);
      return false;
    }
    const landed = await overrideWrite('capacity', Number(trimmed));
    if (landed) cancelEdit();
    return landed;
  }, [capacityDraft, row.capacity, cancelEdit, overrideWrite, refuse, name]);
  // An open capacity editor is a field holding an edit: *Done* and the arrows save it too (R3, R6).
  const capacityCommit = useRef(commitCapacity);
  capacityCommit.current = commitCapacity;
  useEffect(
    () => (editing === 'capacity' ? register(() => capacityCommit.current()) : undefined),
    [editing, register],
  );
  const capacityErrorId = React.useId();

  const editor =
    editing === 'capacity' ? (
      <Input
        aria-label={`Capacity for ${name}`}
        inputMode="numeric"
        size="sm"
        autoFocus
        value={capacityDraft}
        invalid={capacityError !== null}
        aria-describedby={capacityError ? capacityErrorId : undefined}
        disabled={overrideBusy || productBusy}
        onChange={event => {
          setCapacityDraft(event.target.value);
          setCapacityError(null);
        }}
        onKeyDown={event => {
          if (event.key === 'Enter') {
            event.preventDefault();
            void commitCapacity();
          } else if (event.key === 'Escape') {
            // Cancels the editor, never the card (DockedCard skips a prevented Escape).
            event.preventDefault();
            cancelEdit();
          }
        }}
        onBlur={() => void commitCapacity()}
      />
    ) : editing === 'model_3d' ? (
      <div
        ref={modelEditorRef}
        onKeyDown={event => {
          if (event.key === 'Escape') {
            event.preventDefault();
            cancelEdit();
          }
        }}
      >
        <Combobox
          aria-label={`3D model for ${name}`}
          size="sm"
          options={modelOptions}
          filter={MODEL_PICKER_FILTER}
          isExactMatch={MODEL_PICKER_EXACT_MATCH}
          maxResults={Infinity}
          value={row.model_3d ?? undefined}
          loading={modelsLoading}
          loadingText="Loading models…"
          error={modelsError ?? undefined}
          onRetry={() => void loadModels(true)}
          emptyText={modelPickerEmptyText(modelFiles, modelOptions.length)}
          commitOnClose={false}
          disabled={overrideBusy || productBusy}
          /*
           * The field's clear sends null: back to the product's model on a row with a product (what
           * *Use Product's* sends), and no model at all on a row with none — whose plan copy the
           * host then clears too (ruled in the build, 2026-10-20; Rob's 2026-10-06 rule).
           */
          clearLabel={
            row.reference_id
              ? `Use the product’s 3D model for ${name}`
              : `Clear the 3D model for ${name}`
          }
          onChange={value => {
            if (value === null) {
              if (row.model_3d == null) {
                cancelEdit();
                return;
              }
              const rowId = row.id;
              const noProduct = !row.reference_id;
              void overrideWrite('model_3d', null).then(saved => {
                if (!saved) return;
                if (noProduct) onModelCleared?.(rowId);
                cancelEdit();
              });
              return;
            }
            if (value === row.model_3d) {
              cancelEdit();
              return;
            }
            void overrideWrite('model_3d', value).then(ok => {
              if (ok) cancelEdit();
            });
          }}
        />
      </div>
    ) : null;

  const overrideRows = overrideRowsFor(row, name, modelName, {
    onChange: startChange,
    onUseProducts: kind => {
      void overrideWrite(kind, null).then(() => focusOverrideAction(kind));
    },
    // A product write (a first identification goes outside the row's queue) and an override never
    // overlap: identify would put the product's values back over the newer override (Copilot,
    // PR #2177).
    busy: overrideBusy || productBusy,
    editing,
    editor,
  });

  /* ---- Plan (R15; `equipment-page-keep-off-plan-undo`, R8–R10) ---- */
  // The plan wins, as on every other surface (`assetPlanStateFor`): a placed row shows where it is
  // placed whether or not it carries the mark.
  const planIndex = useMemo(() => planIndexFor(layout, planKnown), [layout, planKnown]);
  const planState = planIndex ? assetPlanStateFor(row, planIndex) : null;
  const keptOffPlan = planState === 'keep_off_plan';
  const where = useMemo(() => {
    if (!planKnown) return 'Plan not loaded';
    const placed = placementWhereFor(layout, row.id);
    if (!placed) return 'Not on plan';
    return placed.spaceName ? `${placed.floorName} › ${placed.spaceName}` : placed.floorName;
  }, [planKnown, layout, row.id]);
  // The plan as last drawn, for the put back's Undo: the toast outlives the card (R10).
  const layoutRef = useRef(layout);
  layoutRef.current = layout;
  const planKnownRef = useRef(planKnown);
  planKnownRef.current = planKnown;
  const livePlanStateRef = useRef(livePlanStateOf);
  livePlanStateRef.current = livePlanStateOf;
  const bidFact = (
    <>
      <dt>BID</dt>
      <dd data-none={row.entity_code ? undefined : 'true'}>
        {row.entity_code ? <Mono>{row.entity_code}</Mono> : 'Given when placed'}
      </dd>
    </>
  );
  const [putBackBusyFor, setPutBackBusyFor] = useState<number | null>(null);
  const putBackBusy = putBackBusyFor === row.id;
  // Read synchronously: a second press before the re-render that shows `loading` sends nothing.
  const putBackSending = useRef(new Set<number>());
  // A put back landed and its override row will go: focus moves on to *Costs and Dates* (R9).
  const putBackFocusPending = useRef(false);
  useEffect(() => {
    putBackFocusPending.current = false;
  }, [row.id]);
  const liveRow = (rowId: number) =>
    useEquipmentCatalogStore.getState().registerRows.find(candidate => candidate.id === rowId);
  const undoPutBack = useCallback(
    async (forRow: number, forName: string) => {
      // R7's precondition, read when the Undo runs: still in the register and not placed since. The
      // mark is not checked: the put back's re-read may not have landed, and re-marking is a no-op.
      const outcome = await writes.setFields(
        forRow,
        { keep_off_plan: true },
        {
          precondition: () => {
            const current = liveRow(forRow);
            if (!current) return false;
            // The page's plan as it is now, where it gives one: the card may have closed.
            const live = livePlanStateRef.current;
            const state = live
              ? live(forRow)
              : assetPlanStateFor(current, planIndexFor(layoutRef.current, planKnownRef.current));
            return state !== 'on_plan';
          },
        },
      );
      const said = (tone: 'success' | 'neutral', title: string) =>
        toast.show({
          tone,
          icon: <Icon name={tone === 'success' ? 'eye-slash' : 'circle-info'} />,
          title,
          ...(toastRegion ? { region: toastRegion } : {}),
        });
      if (outcome.ok) return void said('success', `${forName} kept off the plan again`);
      if (outcome.skipped)
        return void said('neutral', `Nothing to undo — ${forName} was changed since`);
      const status = (outcome.error as { status?: unknown } | null)?.status;
      if (status === 400 && KEEP_OFF_UNIDENTIFIED.test(outcome.line)) {
        return void said('neutral', `${forName} lost its product, so left`);
      }
      // A toast, not the card's alert: the Undo outlives the card, which may be closed by now. An
      // unknown outcome may have landed, so it is said as the re-read's note, with nothing to retry.
      const unknown = outcome.line.startsWith(NOT_SURE);
      toast.show({
        tone: 'error',
        icon: <Icon name="circle-exclamation" />,
        title: unknown
          ? `${forName}: ${outcome.line}`
          : `Couldn’t keep ${forName} off the plan again`,
        ...(unknown
          ? {}
          : {
              actions: [{ label: 'Try again', onAction: () => void undoPutBack(forRow, forName) }],
            }),
        ...(toastRegion ? { region: toastRegion } : {}),
      });
    },
    // `liveRow` reads the store, never this render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [writes, toast, toastRegion, refuse],
  );
  const putBack = useCallback(async () => {
    const forRow = row.id;
    const forName = name;
    if (putBackSending.current.has(forRow)) return;
    putBackSending.current.add(forRow);
    setPutBackBusyFor(forRow);
    putBackFocusPending.current = true;
    const outcome = await track(
      writes.setFields(
        forRow,
        { keep_off_plan: false },
        { precondition: () => liveRow(forRow)?.keep_off_plan === true },
      ),
      result => result.ok || ('skipped' in result && !!result.skipped),
    ).finally(() => {
      putBackSending.current.delete(forRow);
      setPutBackBusyFor(current => (current === forRow ? null : current));
    });
    if (outcome.ok) {
      toast.show({
        tone: 'success',
        icon: <Icon name="eye" />,
        title: `${forName} put back on the plan.`,
        sub: 'Floor Plan offers it again.',
        inlineAction: { label: 'Undo', onAction: () => void undoPutBack(forRow, forName) },
        ...(toastRegion ? { region: toastRegion } : {}),
      });
      return;
    }
    // An unknown outcome may have landed: its re-read can still take the override row away, so
    // focus stays pending, and the card says the re-read's note rather than a refusal.
    const unknown = !outcome.skipped && outcome.line.startsWith(NOT_SURE);
    if (!unknown && openRowId.current === forRow) putBackFocusPending.current = false;
    if (outcome.skipped) return;
    // The alert names the row, so an answer for a row the card has left is said there and drawn
    // nowhere else.
    refuse(`Plan of ${forName}: ${unknown ? outcome.line : PUT_BACK_UNSAVED}`);
    // `liveRow` reads the store, never this render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [row.id, name, track, writes, toast, toastRegion, refuse, undoPutBack]);

  /*
   * The put back landed and the re-read took the override row away with its focused action: the
   * card's next control, *Costs and Dates*, takes focus (R9), as after *Keep This Product*.
   */
  useEffect(() => {
    if (keptOffPlan || !putBackFocusPending.current) return;
    putBackFocusPending.current = false;
    const active = document.activeElement;
    if (active && active !== document.body) return;
    costsToggleRef.current?.focus();
  }, [keptOffPlan]);
  /* ---- Costs and Dates, Notes (R16–R17) ---- */
  const costsId = React.useId();
  const notesId = React.useId();
  const filled = filledCount(row);

  const notesField = useCardField(
    row.id,
    asText(row.customer_notes),
    draft => fieldWrite({ customer_notes: draft.trim() === '' ? null : draft }, 'Notes'),
    register,
  );

  /*
   * A Done waiting on a save closes the card it was pressed on, never the next one: moving to
   * another row, or the card closing, makes it stale (Copilot, PR #2177).
   */
  const closeTurn = useRef(0);
  useEffect(() => {
    closeTurn.current += 1;
  }, [row.id]);
  useEffect(
    () => () => {
      closeTurn.current += 1;
    },
    [],
  );
  const done = useCallback(async () => {
    const turn = closeTurn.current;
    if ((await commitPending()) && closeTurn.current === turn) onClose();
  }, [commitPending, onClose]);

  const product = (
    <IdentifyProductField
      // One picker per row: a search, a ranked list or an open list never follows the card to the
      // next row, where Enter would write it there (Copilot, PR #2177).
      key={row.id}
      assetId={row.id}
      answer="brand"
      fetchers={identify.fetchers}
      organisationName={identify.organisationName}
      logContext={identify.logContext}
      busy={productBusy || identify.busy || overrideBusy}
      aside={productAside}
      error={productError ?? undefined}
      value={chosenProduct}
      onChosenChange={(value, option) => void chooseProduct(value, option)}
      current={current}
      proposal={proposal}
      notTrackedOption={!notTracked}
    />
  );

  return (
    <DockedCard
      tone="butter"
      placement="beside"
      icon={<Icon name="pen" />}
      title={name}
      headingLevel={3}
      sub={current?.label ?? (notTracked ? 'Not tracked' : undefined)}
      // × and Escape save the field being edited first, as Done does: closing never drops an edit
      // (Copilot, PR #2177). The page's own closes (a hidden row, a twin change) are not these.
      onClose={() => void done()}
      closeLabel={`Close ${name}`}
      returnFocusTo={returnFocusTo}
      className="equipment-asset-card"
      footer={
        <Foot>
          <span>{removeControl?.(refuse)}</span>
          <Button variant="frosted" iconLeft={<Icon name="check" size="xs" />} onClick={done}>
            Done
          </Button>
        </Foot>
      }
    >
      <Body>
        <Live role="status" aria-live="polite">
          {status}
        </Live>
        <Live role="alert" aria-live="assertive">
          {alert}
        </Live>

        <Field
          label="Name"
          aside="unique in the twin"
          helper={nameField.error ? undefined : (nameLine ?? undefined)}
          error={nameField.error ?? undefined}
        >
          {wiring => (
            <Input
              {...wiring}
              value={nameField.value}
              onChange={event => nameField.onChange(event.target.value)}
              onBlur={() => void nameField.commit()}
              onKeyDown={event => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  void nameField.commit();
                }
              }}
            />
          )}
        </Field>

        <Field
          label="Asset Identifier"
          aside={row.source_asset_id ? `Source id ${row.source_asset_id}` : undefined}
          helper={identifierField.error ? undefined : (identifierLine ?? undefined)}
          error={identifierField.error ?? undefined}
        >
          {wiring => (
            <Input
              {...wiring}
              leadIcon="barcode"
              value={identifierField.value}
              onChange={event => identifierField.onChange(event.target.value)}
              onBlur={() => void identifierField.commit()}
              onKeyDown={event => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  void identifierField.commit();
                }
              }}
            />
          )}
        </Field>

        {product}
        {flagged && (
          <Note
            tone={semantic.status.roles.problem.noteTone}
            icon={<Icon name="triangle-exclamation" size="xs" />}
          >
            <KeepLine>
              <span>
                {fitDetail
                  ? `The name says ${fitDetail.nameKind}; the product is ${fitDetail.productKind}`
                  : 'The name doesn’t fit this product'}
              </span>
              <Button
                variant="frosted"
                size="xs"
                aria-label={`Keep ${keptProduct} for ${name}`}
                // Busy shows as `loading`, never `disabled`: a disabled button drops focus (H1).
                loading={keepBusy}
                disabled={productBusy || identify.busy || overrideBusy}
                onClick={() => void keepProduct()}
              >
                Keep This Product
              </Button>
            </KeepLine>
          </Note>
        )}
        {keepError && (
          <Note tone="error" icon={<Icon name="circle-exclamation" size="xs" />}>
            {keepError}
          </Note>
        )}

        <div ref={overridesRef}>
          <OverrideRows title="From the product" aside={overrideAside(row)} rows={overrideRows} />
          {capacityError && editing === 'capacity' && (
            <Note
              tone="error"
              id={capacityErrorId}
              icon={<Icon name="circle-exclamation" size="xs" />}
            >
              {capacityError}
            </Note>
          )}
        </div>

        {keptOffPlan ? (
          <Section aria-label="Plan">
            <OverrideRows
              title="Plan"
              aside="1 override"
              rows={[
                {
                  id: 'keep_off_plan',
                  label: 'On plan',
                  value: 'Kept off plan',
                  source: 'Floor Plan does not offer this asset',
                  overridden: true,
                  action: {
                    label: 'Put Back On Plan',
                    // The default `{action} {label}` would read *Put Back On Plan On plan*.
                    'aria-label': 'Put Back On Plan',
                    onAction: () => void putBack(),
                    loading: putBackBusy,
                    disabled: productBusy || identify.busy || overrideBusy,
                  },
                },
              ]}
            />
            <Facts>{bidFact}</Facts>
          </Section>
        ) : (
          <Section aria-labelledby={`${costsId}-plan`}>
            <SectionTitle id={`${costsId}-plan`}>Plan</SectionTitle>
            <Facts>
              <dt>Placed</dt>
              <dd data-none={where === 'Not on plan' || !planKnown ? 'true' : undefined}>
                {where}
              </dd>
              {bidFact}
            </Facts>
          </Section>
        )}

        <Folds>
          <FoldToggles>
            <Button
              ref={costsToggleRef}
              variant="text"
              size="sm"
              aria-expanded={folds.costs}
              aria-controls={costsId}
              iconLeft={<Icon name={folds.costs ? 'chevron-down' : 'chevron-right'} size="xs" />}
              onClick={() => onFoldsChange({ ...folds, costs: !folds.costs })}
            >
              {`Costs and Dates · ${filled} of 8 filled`}
            </Button>
            <Button
              variant="text"
              size="sm"
              aria-expanded={folds.notes}
              aria-controls={notesId}
              iconLeft={<Icon name={folds.notes ? 'chevron-down' : 'chevron-right'} size="xs" />}
              onClick={() => onFoldsChange({ ...folds, notes: !folds.notes })}
            >
              Notes
            </Button>
          </FoldToggles>
          {/* Mounted while folded: closing a fold never drops a draft, or a refusal, Done must
              still see (Copilot, PR #2177). */}
          {
            <FoldBody id={costsId} aria-label="Costs and Dates" hidden={!folds.costs}>
              {COST_AND_DATE_FIELDS.map(field =>
                DATE_FIELDS.has(field) ? (
                  <DateCostField key={field} field={field} row={row} save={fieldWrite} />
                ) : (
                  <CostField
                    key={field}
                    field={field}
                    row={row}
                    save={fieldWrite}
                    register={register}
                  />
                ),
              )}
            </FoldBody>
          }
          {
            <FoldBody id={notesId} aria-label="Notes" hidden={!folds.notes}>
              <Field
                label="Notes"
                helper={`${notesField.value.length.toLocaleString('en-GB')} of ${NOTES_MAX.toLocaleString('en-GB')}`}
                error={notesField.error ?? undefined}
              >
                {wiring => (
                  <Textarea
                    {...wiring}
                    maxLength={NOTES_MAX}
                    value={notesField.value}
                    onChange={event => notesField.onChange(event.target.value)}
                    onBlur={() => void notesField.commit()}
                  />
                )}
              </Field>
            </FoldBody>
          }
        </Folds>
      </Body>
    </DockedCard>
  );
});

/** One of the folded typed fields (R16): saved on blur or Enter, as every field is (R6). */
const CostField: React.FC<{
  field: CostAndDateField;
  row: TwinAssetResponse;
  save: (fields: RowWriteFields, label: string) => Promise<string | null>;
  register: (commit: () => Promise<boolean>) => () => void;
}> = ({ field, row, save, register }) => {
  const label = COST_LABELS[field];
  const typed = useCardField(
    row.id,
    asText(row[field]),
    draft => {
      if (field === 'unit_cost' || field === 'residual_value') {
        return save({ [field]: moneyValue(draft) } as RowWriteFields, label);
      }
      if (field === 'currency') {
        const value = draft.trim().toUpperCase();
        return save({ currency: value === '' ? null : value }, label);
      }
      return save({ [field]: draft.trim() === '' ? null : draft.trim() } as RowWriteFields, label);
    },
    register,
  );
  return (
    <Field label={label} error={typed.error ?? undefined}>
      {wiring => (
        <Input
          {...wiring}
          size="sm"
          inputMode={field === 'unit_cost' || field === 'residual_value' ? 'decimal' : undefined}
          maxLength={field === 'currency' ? 3 : undefined}
          value={typed.value}
          onChange={event =>
            typed.onChange(
              field === 'currency' ? event.target.value.toUpperCase() : event.target.value,
            )
          }
          onBlur={() => void typed.commit()}
          onKeyDown={event => {
            if (event.key === 'Enter') {
              event.preventDefault();
              void typed.commit();
            }
          }}
        />
      )}
    </Field>
  );
};

/**
 * One of the four folded dates (R16): a `DateField`, saved on each pick or committed typed date.
 * A refused pick keeps what was picked, with the server's sentence under it, until the row
 * changes; an answer for a row the card has left is drawn nowhere (the alert names its row).
 */
const DateCostField: React.FC<{
  field: CostAndDateField;
  row: TwinAssetResponse;
  save: (fields: RowWriteFields, label: string) => Promise<string | null>;
}> = ({ field, row, save }) => {
  const label = COST_LABELS[field];
  const saved = asText(row[field]);
  const [picked, setPicked] = useState<{ value: string | null } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const shownRow = useRef(row.id);
  shownRow.current = row.id;
  // The latest pick: an earlier pick's answer, landing after a newer pick, is drawn nowhere — its
  // success would clear the newer pick, and the newer one's refusal would sit beside the old date
  // (Copilot, PR #2177).
  const latestPick = useRef(0);
  useEffect(() => {
    setPicked(null);
    setError(null);
  }, [row.id]);
  const errorId = React.useId();
  return (
    <div>
      <DateField
        label={label}
        value={picked ? picked.value : saved === '' ? null : saved.slice(0, 10)}
        invalid={error !== null}
        aria-describedby={error ? errorId : undefined}
        onChange={value => {
          const forRow = row.id;
          const pick = ++latestPick.current;
          setPicked({ value });
          setError(null);
          void save({ [field]: value } as RowWriteFields, label).then(refusal => {
            if (shownRow.current !== forRow || latestPick.current !== pick) return;
            if (refusal === null) setPicked(null);
            else setError(refusal);
          });
        }}
      />
      {error && (
        <Note tone="error" id={errorId} icon={<Icon name="circle-exclamation" size="xs" />}>
          {error}
        </Note>
      )}
    </div>
  );
};
